import type {CheckRunForAnalysis, CIAnalysisJobStatus, CIFinding, CIFindingsResponse} from './ci-findings-types'

export type AnalysisProgress = {
  /** Number of failed check runs that have at least one finding. */
  analyzedCount: number
  /** Total number of failed check runs. */
  failedCount: number
  /** Number of check runs still queued or in progress. */
  pendingCount: number
}

export type CIFindingsData = {
  findings: CIFinding[]
  headSha: string | undefined
  checkRunsForAnalysis: CheckRunForAnalysis[] | undefined
  resultsReady: boolean | undefined
  jobStatus: CIAnalysisJobStatus | undefined
}

type NormalizedCIFindingsData = CIFindingsData & {
  resultsReady: boolean
  checkRunsForAnalysis: CheckRunForAnalysis[]
}

export function adaptCIFindingsResponse(response: CIFindingsResponse): CIFindingsData {
  // Invalid lifecycle metadata becomes undefined; missing discriminators make callers use legacy behavior.
  return {
    findings: response.ciFindings ?? [],
    headSha: response.headSha,
    checkRunsForAnalysis: Array.isArray(response.checkRunsForAnalysis) ? response.checkRunsForAnalysis : undefined,
    resultsReady: typeof response.resultsReady === 'boolean' ? response.resultsReady : undefined,
    jobStatus:
      response.jobStatus === 'pending' || response.jobStatus === 'completed' || response.jobStatus === 'failed'
        ? response.jobStatus
        : undefined,
  }
}

export function hasNormalizedLifecycle(data: CIFindingsData | undefined): data is NormalizedCIFindingsData {
  // These two fields together identify the additive normalized-lifecycle response.
  return typeof data?.resultsReady === 'boolean' && Array.isArray(data.checkRunsForAnalysis)
}

export function computeAnalysisProgress(
  findings: CIFinding[],
  checkRunsForAnalysis: CheckRunForAnalysis[],
): AnalysisProgress {
  if (checkRunsForAnalysis.length === 0) {
    // Without check-run entries, unique finding run IDs are the best available progress signal.
    const analyzedCount = new Set(findings.map(finding => finding.failedCheckRunId)).size
    return {analyzedCount, failedCount: analyzedCount, pendingCount: 0}
  }

  // Failed runs contribute to analysis progress; queued and running checks remain pending.
  const failedRuns = checkRunsForAnalysis.filter(checkRun => checkRun.status === 'failed')
  const pendingRuns = checkRunsForAnalysis.filter(
    checkRun => checkRun.status === 'in_progress' || checkRun.status === 'queued',
  )
  const analyzedIds = new Set(findings.map(finding => finding.failedCheckRunId))

  return {
    analyzedCount: failedRuns.filter(checkRun => analyzedIds.has(checkRun.id)).length,
    failedCount: failedRuns.length,
    pendingCount: pendingRuns.length,
  }
}

export function mergeCIFindingsData(previous: CIFindingsData | undefined, current: CIFindingsData): CIFindingsData {
  if (current.checkRunsForAnalysis === undefined) {
    // Without active-run metadata, we cannot safely decide which previous findings are stale.
    return current
  }

  const activeCheckRunIds = new Set(current.checkRunsForAnalysis.map(checkRun => checkRun.id))

  if (activeCheckRunIds.size === 0) {
    return {...current, findings: []}
  }

  const currentFindings = current.findings.filter(finding => activeCheckRunIds.has(finding.failedCheckRunId))

  if (hasNormalizedLifecycle(current) && current.resultsReady) {
    // Ready normalized results are complete and replace any findings retained while analysis was pending.
    return {...current, findings: currentFindings}
  }

  if (!previous || previous.headSha !== current.headSha) {
    return {...current, findings: currentFindings}
  }

  const previousFindings = previous.findings
  // Pending responses can be partial, so retain prior active findings until replacements arrive.
  const retainedFindings =
    currentFindings.length > 0
      ? previousFindings.filter(finding => activeCheckRunIds.has(finding.failedCheckRunId))
      : previousFindings
  const findingsById = new Map(retainedFindings.map(finding => [finding.id, finding]))

  for (const finding of currentFindings) {
    findingsById.set(finding.id, finding)
  }

  return {...current, findings: [...findingsById.values()]}
}

export function shouldPollForCIFindings(data: CIFindingsData | undefined, pollingExpired: boolean): boolean {
  if (pollingExpired) return false
  if (!data) return true

  if (hasNormalizedLifecycle(data)) {
    // Normalized lifecycle fields provide explicit success and failure terminals.
    return data.jobStatus !== 'failed' && !data.resultsReady
  }

  // Legacy run metadata keeps watching for same-SHA reruns; no findings alone cannot prove completion.
  return data.checkRunsForAnalysis !== undefined || data.findings.length === 0
}

export function isCIFindingsAnalysisPending(data: CIFindingsData | undefined, progress: AnalysisProgress): boolean {
  if (!data) return true

  if (hasNormalizedLifecycle(data)) {
    // The normalized lifecycle directly controls the user-visible pending state.
    return data.jobStatus !== 'failed' && !data.resultsReady
  }

  // Unlike background polling, legacy UI pending is inferred from missing findings or incomplete progress.
  if (data.findings.length === 0) {
    return data.checkRunsForAnalysis === undefined || data.checkRunsForAnalysis.length > 0
  }

  return (
    data.checkRunsForAnalysis !== undefined &&
    (progress.analyzedCount < progress.failedCount || progress.pendingCount > 0)
  )
}
